import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { LoadingButton } from "@mui/lab";
import { Box, CircularProgress, Fade, Grow, Popover, Stack, TextField, TextFieldProps, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import headerImage from "../../../public/images/notissia_share_workspace_header.png";
import { createGroupFunc, createUserFunc, getUserByReferralCodeFunc, getUserFunc } from '../../lib/helper';
import { defaultGeographies, defaultKeyPeopleTags, defaultStages, defaultStatuses, defaultTags, scrollbarStyle } from "../../shared/dashboard";
import { GroupSettings, User } from '../../types/common';
import MultiSelector from "../atoms/MultiSelector";

const useStyles = makeStyles((theme) => ({
    origin: {
        position: 'fixed',
        top: '50%',
        left: '50%',
        width: '100%',
        height: '100%',
    },
    modal: {
        width: '100%',
        height: '100%',
        zIndex: 1300,
        '& > .MuiPaper-root': {
            width: 600,
            height: 'fit-content',
            borderRadius: 24,
            overflow: 'auto',
            ...scrollbarStyle,
        }
    },
    headerImage: {
        backgroundImage: `url(${headerImage})`,
        backgroundSize: 'cover',
        backgroundPosition: '0 -20px',
        height: '100px',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    title: {
        fontFamily: 'Poppins',
        fontWeight: 'bold',
        fontSize: '1.5rem',
        color: '#000',
    },
    subtitle: {
        fontFamily: 'Lato',
        fontWeight: 500,
        fontSize: '0.95rem',
        color: theme.colors.neutral['500'],
    },
    host: {
        fontFamily: 'Inter',
        fontWeight: 'bold',
        fontSize: '0.95rem',
        color: theme.colors.primary['500'],
    },
    group: {
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 'bold',
        color: '#666666',
    },
    label: {
        fontFamily: 'Lato',
        fontSize: '1rem',
        fontWeight: 'bold',
        color: '#666666',
    },
    sublabel: {
        fontFamily: 'Lato',
        fontSize: '1rem',
        fontWeight: 'bold',
        color: theme.colors.neutral['600'],
    },
    optional: {
        fontFamily: 'Lato',
        fontSize: '1rem',
        color: theme.colors.neutral['500'],
    },
    error: {
        fontFamily: 'Lato',
        fontSize: '0.95rem',
        color: theme.colors.error['500'],
    },
    inputField: {
        width: '100%',
        "& .MuiOutlinedInput-root": {
            height: 40,
            borderRadius: 8,
            padding: '0 8px !important',
            background: '#fff',
            "& > input, > textarea": {
                fontFamily: 'Lato',
                fontSize: '1.2rem',
                color: '#048290',
                padding: 'unset !important',
            },
            "& fieldset": {
              	borderColor: 'darkgray !important',
            },
            "&:hover fieldset": {
              	borderColor: 'black !important',
            },
            "&.Mui-focused fieldset": {
              	borderColor: `${theme.palette.primary.main} !important`,
            },
        },
    },
    icon: {
        color: theme.colors.primary['500'],
        width: 50,
        height: 50,
    },
    createButton: {
        minWidth: 80,
        height: 40,
        borderRadius: 32,
        textTransform: 'none',
        fontWeight: 'bold',
        fontSize: '1rem',
        transition: 'ease-in-out 300ms',
    },
}));

const EmailRegex = /^(([^<>()\\[\]\\.,;:\s@"]+(\.[^<>()\\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const ReferralPage: React.FC<{}> = () => {
    const history = useHistory();
    const classes = useStyles();
    const params: {[key: string]: string} = useParams();

    const [user, setUser] = useState<User|undefined>(undefined);
    const [hover, setHover] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>(false);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [selectedStages, setSelectedStages] = useState<string[]>([]);
    const [selectedGeography, setSelectedGeography] = useState<string[]>([]);
    const [saving, setSaving] = useState<boolean>(false);
    const [saveReady, setSaveReady] = useState<boolean>(false);
    const [existingUsername, setExistingUsername] = useState<boolean>(false);
    const [existingEmail, setExistingEmail] = useState<boolean>(false);
    const [existingGroup, setExistingGroup] = useState<boolean>(false);
    const [referralModalOpen, setReferralModelOpen] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const anchorRef = useRef<HTMLDivElement | null>(null);
    const firstNameRef = useRef<TextFieldProps>(null);
    const lastNameRef = useRef<TextFieldProps>(null);
    const emailRef = useRef<TextFieldProps>(null);
    const companyRef = useRef<TextFieldProps>(null);
    const timerRef = useRef<string | number | NodeJS.Timeout | undefined>(undefined);

    const inviteHost = useMemo(() => user?.email, [user]);

    const handleSelect = useCallback((selections: string[], type: string) => {
        const focusArea = { tags: type === 'focusArea' ? selections : selectedTags };
        const stage = { stages: type === 'stage' ? selections : selectedStages };
        const geography = { regions: type === 'geography' ? selections : selectedGeography };

        switch (type) {
            case 'focusArea':
                setSelectedTags(focusArea.tags ?? []);
                break;
            case 'stage':
                setSelectedStages(stage.stages ?? []);
                break;
            case 'geography':
                setSelectedGeography(geography.regions ?? []);
                break;
        }
    }, [selectedTags, selectedStages, selectedGeography]);

    const handleSaveReady = useCallback(() => {
        setSaveReady((!!firstNameRef.current?.value || !!lastNameRef.current?.value) && !!emailRef.current?.value);
        setExistingUsername(false);
        setExistingEmail(false);
        setExistingGroup(false);
    }, []);

    const handleSave = useCallback(() => {
        const newUser: User = {
            email: emailRef.current?.value as string || '',
            firstName: firstNameRef.current?.value as string || '',
            lastName: lastNameRef.current?.value as string || '',
            username: (firstNameRef.current?.value as string || '').replace(' ', '')
                + (lastNameRef.current?.value as string || '').replace(' ', ''),
            createdAt: new Date(),
            updatedAt: new Date(),
        } as User;
        const newGroupSettings: GroupSettings= {
            group: companyRef.current?.value as string || '',
            investmentStages: defaultStages,
            tags: defaultTags,
            tagGroups: [
                {
                    title: 'Sectors',
                    tags: defaultTags,
                    tagsPerDashboard: 3,
                    additionalContextQuery: '',
                }
            ],
            keyPeopleTags: defaultKeyPeopleTags,
            investmentThesis: {
                focusArea: { tags: selectedTags },
                stage: { stages: selectedStages },
                geography: { regions: selectedGeography },
                additionalDetails: null,
            },
            shouldSyncAllDashboardsExternally: true,
            shouldIngestDashboardsFromEmail: true,
            shouldSendDigestEmail: false,
            statuses: defaultStatuses,
            workspaces: [],
            excludedCompanyTerms: [
                companyRef.current?.value as string || '',
            ],
        };

        if (!EmailRegex.test(newUser.email)) {
            enqueueSnackbar('Please enter a valid email address.', {
                anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'right',
                },
                autoHideDuration: 3000,
            });
        } else {
            setSaving(true);
            Promise.all([
                getUserFunc({ email: newUser.email}),
                getUserFunc({ username: newUser.username })
            ]).then(([foundEmail, foundUsername]) => {
                if (!foundEmail && !foundUsername) {
                    createGroupFunc(newGroupSettings).then((newGroupData?: GroupSettings) => {
                        if (!!newGroupData) {
                            createUserFunc(({...newUser, group: newGroupData?.group})).then(() => {
                                setSuccess(true);
                            }).catch(() => setExistingEmail(true))
                            .finally(() => setSaving(false));
                        } else {
                            setExistingGroup(true);
                            setSaving(false);
                        }
                    }).catch(() => {setExistingGroup(true); setSaving(false);});
                } else {
                    if (!!foundUsername)
                        setExistingUsername(true);
                    if (!!foundEmail)
                        setExistingEmail(true);
                    setSaving(false);
                }
            });
        }
    }, [selectedTags, selectedStages, selectedGeography]);

    useEffect(() =>
        setAnchorEl({
            getBoundingClientRect: () => ({
                width: 0,
                height: 0,
                x: window.innerWidth / 2,
                y: window.innerHeight / 2,
                top: window.innerHeight / 2,
                left: window.innerWidth / 2,
                bottom: window.innerHeight / 2,
                right: window.innerWidth / 2,
                toJSON: () => {},
            }),
        } as unknown as HTMLElement)
    , []);

    useEffect(() => {
        const referralId = params.referralId;

        if (!!referralId) {
            timerRef.current = setTimeout(() => {
                clearTimeout(timerRef.current);
                getUserByReferralCodeFunc({referralCode: referralId}).then((user?: User) => {
                    if (!!user) {
                        setUser(user);
                        setReferralModelOpen(true);
                    } else {
                        enqueueSnackbar('Referral code is invalid!', {
                            anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'right',
                            },
                            autoHideDuration: 5000,
                        });
                        history.push('/home');
                    }
                });
            }, 1000);
        } else {
            history.push('/home');
        }

        return () => {
            clearTimeout(timerRef.current);
        };
    // eslint-disable-next-line
    }, [params.referralId]);

    return (<>
        <Stack className={classes.origin} ref={anchorRef}>
            {!referralModalOpen && (<CircularProgress size={40} thickness={5} />)}
        </Stack>
        <Popover open={referralModalOpen}
            className={classes.modal}
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 'center', horizontal: 'center', }}
            transformOrigin={{ vertical: 'center', horizontal: 'center', }}
            PaperProps={{ elevation: hover ? 16 : 12 }}
            TransitionComponent={Grow}
            transitionDuration={500}>
            <Stack className={classes.headerImage} />
            {success ? (
                <Stack direction="column" spacing={2} alignItems="center" width="100%" mt={2} mb={10}
                    onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
                    <Fade in={success} timeout={1000}>
                        <CheckCircleIcon className={classes.icon} sx={{ transform: 'translateX(-5px)' }} />
                    </Fade>
                    <Box />
                    <Typography className={classes.title}>
                        {'Your account is on the way!'}
                    </Typography>
                    <Typography className={classes.subtitle} fontSize="1.1rem !important">
                        {'Check your inbox for your login information.'}
                    </Typography>
                </Stack>
            ) : (
                <Stack spacing={1} width="100%" p={3} pb={0} sx={{ transform: 'translateY(-20px)' }}
                    onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
                    <Stack direction="column" spacing={1} alignItems="flex-start" width="100%">
                        <Typography className={classes.title}>
                            {'Welcome to Notissia'}
                        </Typography>
                        <Stack direction="column" alignItems="flex-start" width="100%">
                            <Typography className={classes.subtitle}>
                                {`You have been referred to join Notissia${inviteHost !== ' ' ? ' by ' : ''}`}
                                <Typography component="span" className={classes.host} display="inline">
                                    {inviteHost}
                                </Typography>.
                            </Typography>
                            <Typography className={classes.subtitle}>
                                {'Please fill out the following information to create your account.'}
                            </Typography>
                        </Stack>
                    </Stack>
                    <Stack direction="column" spacing={1} alignItems="flex-start" width="100%">
                        <Stack spacing="4px" width="100%">
                            <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="flex-start" width="100%">
                                <Stack spacing="4px" width="100%">
                                    <Typography className={classes.label}>{'First Name'}</Typography>
                                    <TextField variant="outlined"
                                        className={classes.inputField}
                                        onChange={handleSaveReady}
                                        inputRef={firstNameRef}
                                        disabled={saving}
                                        rows={1} />
                                </Stack>
                                <Stack spacing="4px" width="100%">
                                    <Typography className={classes.label}>{'Last Name'}</Typography>
                                    <TextField variant="outlined"
                                        className={classes.inputField}
                                        onChange={handleSaveReady}
                                        inputRef={lastNameRef}
                                        disabled={saving}
                                        rows={1} />
                                </Stack>
                            </Stack>
                            {existingUsername && (
                                <Typography className={classes.error}>
                                    {'This user name has an account. Try signing in and resetting your password.'}
                                </Typography>
                            )}
                        </Stack>
                        <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="flex-start" width="100%">
                            <Stack spacing="4px" width="100%">
                                <Typography className={classes.label}>{'Work email'}</Typography>
                                <TextField variant="outlined" type="email"
                                    className={classes.inputField}
                                    onChange={handleSaveReady}
                                    inputRef={emailRef}
                                    disabled={saving}
                                    rows={1} />
                                {existingEmail && (
                                    <Typography className={classes.error}>
                                        {'This email address has an account. Try signing in and resetting your password.'}
                                    </Typography>
                                )}
                            </Stack>
                        </Stack>
                        <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="flex-start" width="100%">
                            <Stack spacing="4px" width="100%">
                                <Typography className={classes.label}>{'Fund or Company Name'}</Typography>
                                <TextField variant="outlined"
                                    className={classes.inputField}
                                    onChange={handleSaveReady}
                                    inputRef={companyRef}
                                    disabled={saving}
                                    rows={1} />
                                {existingGroup && (
                                    <Typography className={classes.error}>
                                        {'This company already has an account. Please choose another company name'}<br/>
                                        {'or ask your team member to add you to their workspace.'}
                                    </Typography>
                                )}
                            </Stack>
                        </Stack>
                    </Stack>
                    <Stack direction="column" spacing={2} alignItems="flex-start" width="100%">
                        <Typography className={classes.group} mt={1}>
                            {'Deal Preferences'}
                        </Typography>
                        <MultiSelector
                            mainOptions={defaultTags}
                            selections={selectedTags}
                            label={(<Typography className={classes.sublabel}>
                                    {'Sectors of interest'}
                                    <Typography component="span" className={classes.optional}>
                                        {' (optional)'}
                                    </Typography>
                                </Typography>)}
                            onSelection={(selections) => handleSelect(selections as string[], 'focusArea')}
                            withSearch />
                        <MultiSelector
                            mainOptions={defaultStages}
                            selections={selectedStages}
                            label={(<Typography className={classes.sublabel}>
                                    {'Stage(s)'}
                                    <Typography component="span" className={classes.optional}>
                                        {' (optional)'}
                                    </Typography>
                                </Typography>)}
                            onSelection={(selections) => handleSelect(selections as string[], 'stage')}
                            withSearch />
                        <MultiSelector
                            mainOptions={defaultGeographies}
                            selections={selectedGeography}
                            label={(<Typography className={classes.sublabel}>
                                    {'Geography'}
                                    <Typography component="span" className={classes.optional}>
                                        {' (optional)'}
                                    </Typography>
                                </Typography>)}
                            onSelection={(selections) => handleSelect(selections as string[], 'geography')}
                            withSearch />
                        <Box />
                    </Stack>
                    <Stack direction="column" spacing={1} alignItems="center" width="100%">
                        <LoadingButton variant="contained"
                            className={classes.createButton}
                            loading={saving}
                            disabled={!saveReady}
                            onClick={handleSave}
                        > {'Create my account'} </LoadingButton>
                    </Stack>
                </Stack>
            )}
        </Popover>
    </>);
}

export default ReferralPage;